import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';

//Components
import {
    InputNumeric,
    FileUpload,
    TextArea,
    Select,
    Button,
    Input,
    Modal,
    Form,
} from "@gpstrackit/gtc-react-controls";
import { NotificationContainer } from "Components/Notifications";
import { NotificationManager } from 'Components/Notifications';
import { Grid } from '@mui/material';

//Actions
import { uploadAttachment } from 'Redux/actions';
import {
    maintenanceMCDTCreateMaintenanceDriverTask,
    maintenanceMCDTGetMaintenanceDriverTask,
    maintenanceMCDTUpdateMaintenanceDriverTask,
    reset,
} from './redux/slice';

//Styles
import './styles.scss';
const CreateEditDriverTask = (props) => {
    const { messages } = useIntl();
    const formRef = Form.useForm();
    const dispatch = useDispatch();

    const OPTIONS = [
        { id: 'Hours', name: messages['driversTasksTab_hours'] },
        { id: 'Days', name: messages['driversTasksTab_days'] },
        { id: 'Months', name: messages['driversTasksTab_months'] }
    ];

    //Redux States
    const {
        updateDriverTaskResponse,
        createDriverTaskResponse,
        driverTasks,
        open,
        loading,
        taskId,
        error,
    } = useSelector(state => state.maintenanceCreateDriverTasksRedux);
    const {
        uploadedAttachment,
        loadingUploadFile,
    } = useSelector(state => state.commomRedux);

    //Local States
    const [formData, setFormData] = useState({ fileIds: [] });

    useEffect(() => {
        if (open && formRef?.ref && driverTasks) {
            let timeUnit = OPTIONS.find((item) => item.id === driverTasks?.timeUnit)
            let timer = setTimeout(() => {
                let dataDriverTasks = {
                    name: driverTasks?.name,
                    estimateCost: driverTasks?.estimatedCost,
                    timeUnit: timeUnit,
                    frequency: driverTasks?.frequency,
                    fileIds: driverTasks?.files,
                    description: driverTasks?.description,
                }
                formRef.setValues(dataDriverTasks);
                setFormData(dataDriverTasks);
                timer = null;
            }, 100);
        }
    }, [driverTasks, formRef?.ref])

    useEffect(() => {
        if (createDriverTaskResponse && createDriverTaskResponse.id) {
            NotificationManager.success(
                messages['success'],
                messages['scheduleTask_saved_select'],
                3000,
                null,
                null,
                'success',
            );
            dispatch(reset({ createDriverTaskResponse: null }));
            handleClose();
        }
        if (updateDriverTaskResponse && updateDriverTaskResponse?.id) {
            NotificationManager.success(
                messages['success'],
                messages['scheduleTask_saved_select'],
                3000,
                null,
                null,
                'success',
            );
            dispatch(reset({ createDriverTaskResponse: null }));
            handleClose();
        }
        if (error) {
            NotificationManager.error(
                error?.error || messages['errorMessage'],
                messages['error'],
                3000,
                null,
                null,
                'error',
            );
            dispatch(reset({ error: null }));
        }

    }, [createDriverTaskResponse, updateDriverTaskResponse, error]);

    useEffect(() => {
        if (taskId) {
            dispatch(maintenanceMCDTGetMaintenanceDriverTask({ id: taskId }))
        }
    }, [taskId])

    const handleDriverTaskSubmit = () => {
        let ids = formData?.fileIds.length ? formData?.fileIds.map((item) => item.id) : []
        let driverTask = {
            ...formData,
            name: formData?.name,
            timeUnit: formData?.timeUnit?.id,
            estimatedCost: formData?.estimateCost || 0,
            frequency: formData?.frequency,
            description: formData?.description,
            fileIds: ids,
        };

        if (taskId) {
            driverTask = {
                ...driverTask,
                id: taskId,
            }
            dispatch(maintenanceMCDTUpdateMaintenanceDriverTask(driverTask))
        } else {
            dispatch(maintenanceMCDTCreateMaintenanceDriverTask(driverTask))
        }
    };

    const handleClose = () => {
        formRef?.resetValues();
        dispatch(reset());
        setFormData({ fileIds: [] });
    }

    const getMax = useCallback((newTimeUnit) => {
        if (newTimeUnit === "Days") {
            return 999;
        } else if (newTimeUnit === "Months") {
            return 120;
        } else if (newTimeUnit === "Hours") {
            return 99999;
        }

        return 0;
    });

    return (
        <Modal
            title={driverTasks ? driverTasks.name : messages['driversTasksTab_createDriverTask']}
            open={open}
            handleClose={handleClose}
            position="right"
            size="small"
            loading={loading}
            closeButton={false}
            footer={(
                <div>
                    <Button
                        variant="secondary"
                        onClick={handleClose}
                        style={{ marginRight: "5px" }}
                        isDisabled={loading}
                    >
                        {messages["cancel"]}
                    </Button>

                    <Button
                        onClick={(e) => formRef.submit()}
                        variant="primary"
                        isDisabled={loading}
                    >
                        {driverTasks ? `${messages["save"]}` : `${messages["add"]}`}
                    </Button>
                </div>
            )}
        >
            <div className="create-driver-tasks-container">
                <p className="contentBack" onClick={handleClose}>
                    {'<'} <span className="backBtn">{messages["back"]}</span>
                </p>
                <Form ref={formRef.ref} onValidSubmit={handleDriverTaskSubmit}>
                    <Form.Item
                        name="name"
                        label={messages['driversTasksTab_taskLabel']}
                        rules={[{ required: true, message: 'required_field' }]}
                    >
                        <Input
                            id="name"
                            type="text"
                            placeholder={messages['scheduleDriverTask_taskNameInputPlaceholder']}
                            autoFocus
                            maxLength={30}
                            onChange={(value) => setFormData({ ...formData, name: value })}
                        />
                    </Form.Item>
                    <Form.Item
                        name="estimateCost"
                        label={messages['driversTasksTab_estimatedCostLabel']}
                    >
                        <InputNumeric
                            id="estimateCost"
                            type="currency"
                            placeholder={messages['driversTasksTab_estimatedCostPlaceholder']}
                            maxLength={7}
                            min={0}
                            onChange={(value) => {
                                value = value.replace(/[_-]/g, "");
                                setFormData({ ...formData, estimateCost: parseFloat(value) })
                                setTimeout(() => { formRef.setValues({ estimateCost: value }) }, 50);
                            }}
                            decimalPlaces={2}
                        />
                    </Form.Item>
                    <Grid container spacing={1}>
                        <Grid item xs={8}>
                            <Form.Item
                                name="timeUnit"
                                label={messages['driversTasksTab_repeat']}
                                rules={[{ required: true, message: messages['required_field'] }]}
                            >
                                <Select
                                    id="timeUnit"
                                    options={OPTIONS}
                                    textField="name"
                                    placeholder={messages['driversTasksTab_repeatPlaceHolder']}
                                    valueField="id"
                                    defaultValue={OPTIONS[0]}
                                    isClearable
                                    onChange={(value) => setFormData({ ...formData, timeUnit: value })}
                                    className="create-driver-tasks-selector"
                                />
                            </Form.Item>
                        </Grid>
                        <Grid item xs={4}>
                            <Form.Item
                                name="frequency"
                                label={messages['driversTasksTab_frequency']}
                                rules={[
                                    {
                                        required: true,
                                        message: messages['required_field']
                                    },
                                    {
                                        max: getMax(formData?.timeUnit?.id),
                                        message: `${messages['max']}: ${getMax(formData?.timeUnit?.id)}`
                                    },
                                    {
                                        min: 0,
                                        message: `${messages['min']}: 0`
                                    }
                                ]}
                            >
                                <InputNumeric
                                    id="frequency"
                                    type="number"
                                    placeholder={messages['driversTasksTab_frequencyPlaceHolder']}
                                    maxLength={3}
                                    onChange={(value) => setFormData({ ...formData, frequency: parseInt(value) })}
                                    isDisabled={!formData?.timeUnit?.id}
                                />
                            </Form.Item>
                        </Grid>
                    </Grid>
                    <Form.Item
                        name="fileIds"
                        label={messages['updateService_attachmentsInputLabel']}
                    >
                        <FileUpload
                            id="fileIds"
                            value={formData?.fileIds}
                            placeholder={messages['updateService_attachmentsInputPlaceholder']}
                            types="image/*,.txt,.doc,.docx,.pdf,.csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                            maxSize={4}
                            maxSizeMessage={`${messages['max']}: 4MB`}
                            helpText={messages['attachmentsFilesAllowed']}
                            uploadAttachment={(data) => { dispatch(uploadAttachment(data)) }}
                            uploadedAttachment={uploadedAttachment}
                            loadingUploadFile={loadingUploadFile}
                            onChange={(value) => setFormData({ ...formData, fileIds: value })}
                        />
                    </Form.Item>
                    <Form.Item
                        label={messages['scheduleDriverTask_notesInputLabel']}
                        name="description"
                    >
                        <TextArea
                            id="description"
                            placeholder={messages['scheduleDriverTask_notesInputPlaceholder']}
                            maxLength={250}
                            onChange={(value) => setFormData({ ...formData, description: value })}
                        />
                    </Form.Item>
                </Form>
            </div>
            <NotificationContainer />
        </Modal>
    );
};

export default CreateEditDriverTask

